import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon, CloseIcon } from "./icons";
import { PANE_HEIGHT, TAB_BAR_MARGIN } from "../app/chrome.js";
import { cn } from "../lib/cn";
import { useT } from "../i18n/index.js";

/**
 * The one modal shape this app has: a card that comes up from the bottom edge.
 *
 * Extracted from the three sheets that already did this by hand — a wish, a
 * task, an event — because making something new now opens the same thing.
 * Creating and editing are the same act at two moments, and until now they
 * looked nothing alike: editing opened a sheet, while making a new one grew a
 * form inline, in a different place on every screen.
 *
 * What the shape carries, and why each part is here rather than at the call
 * sites that used to repeat it:
 *
 * - **A ceiling of `PANE_HEIGHT`**, so the sheet stops above the floating tab
 *   bar and below the header rather than under either.
 * - **`min-h-0` on the body**, or a flex child refuses to go below its content
 *   height and the last field is simply cut off.
 * - **`overflow-x-hidden` with it.** CSS says that when one axis is `visible`
 *   and the other is not, the visible one computes to `auto` — so a sheet that
 *   only wanted vertical scrolling silently became a sideways scroll container.
 * - **Focus on the sheet itself**, not on the first field: on a phone, focusing
 *   a text box throws the keyboard over half of what was just opened.
 * - **Escape and the dimmed background both mean "back"**, and the page behind
 *   does not scroll while this is up.
 * - **It comes up from the edge and goes back down to it.** A modal that
 *   simply exists, with no travel, reads as a page that replaced the one
 *   underneath; the travel is what says «this is on top of what you had, and
 *   it will give it back». It is also what makes the gesture below legible.
 * - **A drag down from the header puts it away**, following the finger,
 *   because that is what a card that came up from the bottom edge invites. The
 *   header and not the whole sheet: the body scrolls, and a gesture that both
 *   scrolls and dismisses has to guess which was meant.
 *
 * «Away» means two different things depending on what is in it. A sheet with
 * `onMinimise` is a form somebody has started, and the drag pushes it down to
 * the line above the tab bar with everything still in it (`app/draft`); the
 * cross, which is the deliberate gesture, throws it away. A sheet without one
 * is just closed. Escape and the dimmed background follow the drag rather than
 * the cross: neither is a decision to discard anything.
 */

/** Long enough to read as movement, short enough not to be waited on. */
const TRAVEL_MS = 260;

/** How far down the header has to be dragged before letting go closes it. */
const DISMISS_PX = 96;

/** …or how fast, for a short flick that never reaches the distance. */
const DISMISS_VELOCITY = 0.5;
export function Sheet({
  title,
  onClose,
  onMinimise,
  action,
  children,
}: {
  title: string;
  onClose: () => void;
  /** Present when what is inside is worth keeping — see above. */
  onMinimise?: (() => void) | undefined;
  /** The commit button, which lives in the header and not in the scrolling body. */
  action?: ReactNode;
  children: ReactNode;
}) {
  const t = useT();
  const titleId = useId();
  const sheetRef = useRef<HTMLDivElement>(null);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const putAwayRef = useRef(onMinimise ?? onClose);
  putAwayRef.current = onMinimise ?? onClose;

  const [leaving, setLeaving] = useState(false);
  /** How far the finger has taken it, while a finger is on it. */
  const [dragged, setDragged] = useState(0);
  const [dragging, setDragging] = useState(false);
  /**
   * True once the entrance has finished, and from then on position is a
   * transform rather than a keyframe.
   *
   * The two cannot share the job. A keyframe with `both` keeps applying its
   * end value, which beats an inline transform — so a drag has to switch the
   * animation off, and switching it back on afterwards *restarts it*: the
   * sheet dropped to the bottom edge and climbed back up, which is the flicker
   * this was reported as. One entrance, then arithmetic.
   */
  const [settled, setSettled] = useState(false);
  const drag = useRef<{ from: number; at: number; when: number } | null>(null);

  /** Down to the edge first, gone afterwards. */
  const dismiss = useCallback(() => {
    if (leaving) return;
    setSettled(true);
    setDragging(false);
    setLeaving(true);
    setTimeout(() => closeRef.current(), TRAVEL_MS);
  }, [leaving]);

  /** The same travel, ending in a line above the tab bar instead of nothing. */
  const putAway = useCallback(() => {
    if (leaving) return;
    setSettled(true);
    setDragging(false);
    setLeaving(true);
    setTimeout(() => putAwayRef.current(), TRAVEL_MS);
  }, [leaving]);

  useEffect(() => {
    sheetRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      putAway();
    };

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [putAway]);

  /* The handle and the header, followed by a finger. */
  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    // Not from a button — the close, the collapse and the commit are targets
    // of their own.
    if ((event.target as HTMLElement).closest("button")) return;
    drag.current = { from: event.clientY, at: event.clientY, when: event.timeStamp };
    setSettled(true);
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const held = drag.current;
    if (!held) return;
    held.at = event.clientY;
    // Downwards only. Dragging a bottom sheet upwards is not a gesture, it is
    // a mistake, and following it would promise something that cannot happen.
    setDragged(Math.max(0, event.clientY - held.from));
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    const held = drag.current;
    drag.current = null;
    if (!held) return;

    const travelled = Math.max(0, held.at - held.from);
    const speed = travelled / Math.max(1, event.timeStamp - held.when);

    setDragging(false);

    if (travelled > DISMISS_PX || speed > DISMISS_VELOCITY) {
      // The offset stays exactly where the finger let go, and the travel down
      // continues from there. Resetting it first is what made this jerk: the
      // sheet snapped back up and only then began to leave.
      putAway();
      return;
    }

    setDragged(0);
  };

  return createPortal(
    <>
      {/* Black rather than a token: dimming has to read as shade in both
          themes, and every surface token flips between them. */}
      <div
        onClick={putAway}
        aria-hidden="true"
        className={cn(
          "fixed inset-0 z-40 bg-black/45",
          leaving ? "vm-scrim-leaving" : "vm-scrim",
        )}
      />

      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          "pad-safe-x fixed inset-x-0 z-40 mx-auto w-full max-w-2xl",
          "flex flex-col overflow-hidden rounded-t-card bg-surface shadow-soft outline-none",
          // The entrance, and only the entrance.
          !settled && !leaving && "vm-sheet",
        )}
        onAnimationEnd={() => setSettled(true)}
        style={{
          /*
           * Above the keyboard, not under it.
           *
           * `bottom-0` is the bottom of the *layout* viewport, and on iOS that
           * does not move when a keyboard comes up — the visual viewport
           * shrinks instead, so a sheet pinned to the bottom had its last
           * fields and its buttons behind the keys. `--keyboard-inset` is that
           * measurement (see `app/keyboard`), and it is nought on a laptop and
           * on Android, where the layout viewport shrinks by itself.
           */
          bottom: "var(--keyboard-inset, 0px)",
          maxHeight: PANE_HEIGHT,
          paddingBottom: `calc(var(--safe-bottom) + ${TAB_BAR_MARGIN}px)`,
          ...(settled || leaving
            ? {
                transform: leaving ? "translateY(100%)" : `translateY(${dragged}px)`,
                // No transition under a finger: it is already following one.
                transition: dragging ? "none" : `transform ${TRAVEL_MS}ms ease-out`,
              }
            : {}),
        }}
      >
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="shrink-0 touch-none"
        >
          {/* The grab handle. It does nothing on its own — it is there to say
              that the top edge is a thing you can take hold of, which a
              gesture cannot say for itself. */}
          <div className="flex justify-center pt-2 pb-1">
            <span aria-hidden="true" className="bg-line h-1 w-9 rounded-full" />
          </div>

          <header className="border-line-soft flex items-center gap-2 border-b px-3 pt-1 pb-2.5">
          {/* Closing is on the left, the commit on the right — the same sides
              the editing sheets already use, so the two never swap places. */}
          <button
            type="button"
            onClick={dismiss}
            aria-label={t("common.close")}
            className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface-alt hover:text-ink"
          >
            <CloseIcon className="size-[20px]" strokeWidth={1.9} />
          </button>

          <h2 id={titleId} className="min-w-0 flex-1 truncate font-display text-[1.0625rem]">
            {title}
          </h2>

            {/* Spelled out for anybody who does not know the gesture, and for
                anybody who cannot make it — a pointer that is not a finger. */}
            {onMinimise ? (
              <button
                type="button"
                onClick={putAway}
                aria-label={t("common.collapse")}
                title={t("common.collapse")}
                className="text-muted hover:bg-surface-alt hover:text-ink grid size-[var(--touch)] shrink-0 place-items-center rounded-pill transition-colors"
              >
                <ChevronDownIcon className="size-[20px]" strokeWidth={1.9} />
              </button>
            ) : null}

            {action}
          </header>
        </div>

        <div className="flex min-h-0 flex-col overflow-x-hidden overflow-y-auto overscroll-contain">
          {children}
        </div>
      </div>
    </>,
    document.body,
  );
}
